import { useEffect } from "react";
import { useCarConfigSelector } from "../redux/store";
import { updateTrackingBaseLayer } from "../../../../common-deprecated/utils/tracking";
import { updateTrackingLayerVersioning } from "../utils/tracking";

const getArrayFilledFlag = (items: unknown[]): 0 | 1 => (items.length ? 1 : 0);

export const useAphBaseLayerTracking = (): void => {
    const car = useCarConfigSelector((store) => store.car);
    const basket = useCarConfigSelector((store) => store.basket);
    const tyCode = useCarConfigSelector(
        (store) => store.save?.data?.Personal?.Code || store.save.data?.ConfigurationCode || "",
    );
    const currencyCode = useCarConfigSelector((store) => store.commonSettings.intl.currencyCode);

    const tmeModelId = car.model.internalCode.toLowerCase();
    const modelName = car.model.name;
    const price = car.price.discount.toFixed(2);
    const totalPrice = basket.total.discount.toFixed(2);
    const {
        colour,
        wheel,
        upholstery,
        packs,
        extras,
        financePackExtras,
        serviceProducts,
        financePackServiceProducts,
        financePacks,
    } = basket;

    const gradeId = car.grade.id;
    const gradeName = car.grade.name;

    const engineId = `${car.motorization.engine.internalCode}${car.motorization.transmission.internalCode}${car.motorization.wheeldrive.internalCode}`;
    const engineName = car.motorization.engine.name;
    const engineType = car.motorization.engine.typeId;
    const isHybrid = car.motorization.engine.hybrid ? 1 : 0;
    const isPlugin = car.motorization.engine.plugin ? 1 : 0;

    const bodytypeId = car.body.id;
    const bodytypeName = car.body.name;

    const transmissionId = car.motorization.transmission.id;
    const transmissionName = car.motorization.transmission.name;

    const accessories = basket.extras.filter((extra) => extra.type === "accessory") || [];
    const options = basket.extras.filter((extra) => extra.type === "option") || [];

    useEffect(() => {
        updateTrackingLayerVersioning();
    }, []);

    useEffect(() => {
        updateTrackingBaseLayer({
            configuration: {
                type: "carconfig",
                modelId: tmeModelId,
                accessories: getArrayFilledFlag(accessories),
                options: getArrayFilledFlag(options),
                packs: getArrayFilledFlag(packs || []),
                services: getArrayFilledFlag(financePacks || []),
                carPrice: price,
                totalPrice,
                isPlugin,
                isHybrid,
                toyotaCode: tyCode,
            },
            product: [
                {
                    category: "cars",
                    subCategory: "new-cars",
                    productInfo: {
                        productId: car.id,
                        productName: `${car.model.name} ${car.grade.name} ${car.body.name}`,
                        price,
                        currencyCode,
                        tmeModelId,
                        modelName,
                        gradeId,
                        gradeName,
                        engineId,
                        engineName,
                        engineType,
                        engineIsPlugin: isPlugin,
                        engineIsHybrid: isHybrid,
                        bodytypeId,
                        bodytypeName,
                        transmissionId,
                        transmissionName,
                    },
                },
                {
                    category: "colours",
                    subCategory: "exterior",
                    productInfo: {
                        productId: colour.code,
                        productName: colour.name,
                        price: colour.price.discount.toFixed(2),
                        currencyCode,
                    },
                },
                {
                    category: "upholsteries",
                    subCategory: "interior",
                    productInfo: {
                        productId: upholstery.id,
                        productName: upholstery.name,
                        price: upholstery.price.discount.toFixed(2),
                        currencyCode,
                    },
                },

                {
                    category: "wheels",
                    productInfo: {
                        productId: wheel.id,
                        productName: wheel.name,
                        price: wheel.price.discount.toFixed(2),
                        currencyCode,
                    },
                },
                ...packs.map((pack) => ({
                    category: "packs",
                    productInfo: {
                        productId: pack.id,
                        productName: pack.name,
                        price: pack.price.discount.toFixed(2),
                        currencyCode,
                    },
                })),
                ...[...extras, ...financePackExtras].map((extra) => ({
                    category: "accessories",
                    subCategory: extra.category.root?.code === "EXT" ? "exterior" : "interior",
                    productInfo: {
                        productId: extra.id,
                        productName: extra.name,
                        price: extra.price.discount.toFixed(2),
                        currencyCode,
                    },
                })),
                ...[...serviceProducts, ...financePackServiceProducts, ...financePacks].map((service) => ({
                    category: "services",
                    productInfo: {
                        productId: service.id,
                        productName: service.name,
                        price: service.price.discount.toFixed(2),
                        currencyCode,
                    },
                })),
            ],
        });
    }, [
        tmeModelId,
        car,
        gradeId,
        gradeName,
        engineId,
        engineName,
        engineType,
        bodytypeId,
        bodytypeName,
        transmissionId,
        transmissionName,
        accessories,
        options,
        packs,
        financePacks,
        price,
        totalPrice,
        isHybrid,
        tyCode,
        colour,
        upholstery,
        wheel,
    ]);
};
